import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BASE_END_POINT_INVOICE,
  INVOICE_END_POINT,
  CREATE_PAYMENT_END_POINT,
  CREATE_SUBSCRIPTION_END_POINT,
  SUBSCRIPTION_APPROVAL_END_POINT,
  SUBSCRIPTION_PROCESS_END_POINT
} from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import {
  createSubscriptionSucceeded,
  createSubscriptionFailed,
  createSubscriptionBillSucceeded,
  processSubscriptionBillSucceeded,
  isLoadingUpdated
} from '../invoice/invoicesSlice';
import { jobApi } from './jobsApiSlice';
import { clearUserJobs } from '../job/jobsSlice';

export const invoiceApi = createApi({
  reducerPath: 'invoiceApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_END_POINT_INVOICE}${INVOICE_END_POINT}`
  }),
  endpoints: (build) => ({
    createPayment: build.mutation({
      query: ({ userId, email, payment_method, currency, items, job_id, metadata }) => {
        const localAuth = getLocalStorageAuthentication();
        
        // Validate fullName is present
        if (!localAuth?.fullName) {
          console.error('⚠️ fullName is missing from JWT. User needs to log out and log back in.');
        }
        
        return {
          url: CREATE_PAYMENT_END_POINT,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: {
            userId,
            email,
            full_name: localAuth?.fullName || 'User',
            payment_method,
            currency,
            items,
            job_id,
            metadata
          }
        };
      },
      // Effects to trigger on success/error
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        dispatch(isLoadingUpdated({ isLoading: true }));
        
        const errorMessage = () => {
          dispatch(createSubscriptionFailed());
          dispatch(
            messageUpdated({
              text: 'Payment failed, please try again',
              severity: Severity.ERROR
            })
          );
        };
        
        try {
          const { data } = await queryFulfilled;
          const payment = data?.data || data;
          
          if (payment) {
            dispatch(
              createSubscriptionSucceeded({
                subscription: payment
              })
            );
            dispatch(
              messageUpdated({
                text: "✅ Payment received successfully! Your job post is now active and visible to candidates.",
                severity: Severity.SUCCESS
              })
            );
            // Clear user jobs so dashboard will refetch on next visit
            dispatch(clearUserJobs());
            // Manually invalidate jobs cache to refresh dashboard
            dispatch(jobApi.util.invalidateTags([{ type: 'Jobs', id: arg.userId }]));
          } else {
            errorMessage();
          }
        } catch (error) {
          console.error('Payment creation failed:', error);
          console.error('Error response:', error.error);
          console.error('Error status:', error.error?.status);
          console.error('Error data:', error.error?.data);
          errorMessage();
        }
      }
    }),
    
    createSubscription: build.mutation({
      query: ({ userId, email, payment_method, items }) => {
        const localAuth = getLocalStorageAuthentication();
        
        // Validate fullName is present
        if (!localAuth?.fullName) {
          console.error('⚠️ fullName is missing from JWT. User needs to log out and log back in.');
        }
        
        return {
          url: CREATE_SUBSCRIPTION_END_POINT,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: {
            userId,
            email,
            full_name: localAuth?.fullName || 'User',
            payment_method,
            items
          }
        };
      },
      // Effects to trigger on success/error
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        dispatch(isLoadingUpdated({ isLoading: true }));
        
        const errorMessage = () => {
          dispatch(createSubscriptionFailed());
          dispatch(
            messageUpdated({
              text: 'Subscription not created, please try again',
              severity: Severity.ERROR
            })
          );
        };
        
        try {
          const { data } = await queryFulfilled;
          const subscription = data?.data || data;
          
          if (subscription) {
            dispatch(
              createSubscriptionSucceeded({
                subscription
              })
            );
            dispatch(
              messageUpdated({
                text: "✅ Subscription activated! Your payment method has been securely saved for recurring billing.",
                severity: Severity.SUCCESS
              })
            );
            // Clear user jobs so dashboard will refetch on next visit
            dispatch(clearUserJobs());
            // Manually invalidate jobs cache to refresh dashboard
            dispatch(jobApi.util.invalidateTags([{ type: 'Jobs', id: arg.userId }]));
          } else {
            errorMessage();
          }
        } catch (error) {
          console.error('Subscription creation failed:', error);
          console.error('Error response:', error.error);
          console.error('Error status:', error.error?.status);
          console.error('Error data:', error.error?.data);
          errorMessage();
        }
      }
    }),
    
    createSubscriptionBill: build.mutation({
      query: ({ subscriptionBill }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: SUBSCRIPTION_APPROVAL_END_POINT,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: subscriptionBill
        };
      },
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Subscription bill not created, please try again',
              severity: Severity.ERROR
            })
          );
        };
        
        try {
          const { data } = await queryFulfilled;
          const subscriptionApprovalUrl = data?.data?.subscriptionApprovalUrl || data?.subscriptionApprovalUrl;
          
          if (subscriptionApprovalUrl) {
            dispatch(
              createSubscriptionBillSucceeded({
                subscriptionApprovalUrl
              })
            );
            dispatch(
              messageUpdated({
                text: 'Subscription bill created successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch (error) {
          errorMessage();
        }
      }
    }),
    
    processSubscriptionBill: build.mutation({
      query: ({ processSubscriptionBill }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: SUBSCRIPTION_PROCESS_END_POINT,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: processSubscriptionBill
        };
      },
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'Subscription process failed, please try again',
              severity: Severity.ERROR
            })
          );
        };
        
        try {
          const { data } = await queryFulfilled;
          const isProcessedSubscription = data?.data?.isProcessedSubscription || data?.isProcessedSubscription;
          
          if (isProcessedSubscription !== undefined) {
            dispatch(
              processSubscriptionBillSucceeded({
                isProcessedSubscription
              })
            );
            dispatch(
              messageUpdated({
                text: 'Subscription processed successfully',
                severity: Severity.SUCCESS
              })
            );
            // Clear user jobs so dashboard will refetch on next visit
            dispatch(clearUserJobs());
            // Manually invalidate jobs cache to refresh dashboard
            const localAuth = getLocalStorageAuthentication();
            dispatch(jobApi.util.invalidateTags([{ type: 'Jobs', id: localAuth.userId }]));
          } else {
            errorMessage();
          }
        } catch (error) {
          errorMessage();
        }
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useCreatePaymentMutation,
  useCreateSubscriptionMutation,
  useCreateSubscriptionBillMutation,
  useProcessSubscriptionBillMutation
} = invoiceApi;

